import FrontdoorInternalService from "./FrontdoorInternalService";

/**
 * BaseFrontdoorAdapter
 *
 * The inheritable base for all Frontdoor REST-based adapters.
 * Provides clean CRUD verbs that delegate to the FrontdoorInternalService.
 *
 * Usage:
 *   class FrontdoorUserAdapter extends BaseFrontdoorAdapter {
 *     async getAll() {
 *       return this.get('/users');
 *     }
 *   }
 */
export class BaseFrontdoorAdapter {
  constructor(options = {}) {
    this.options = options;
    this.companyKey = options.companyKey || options.companykey;
  }

  /**
   * GET request with optional query parameters.
   * @param {string} resource - The endpoint path e.g. '/users'
   * @param {Object} params - Optional key/value query params
   * @param {Object} options - Optional fetch options (e.g. headers)
   */
  get(resource, params = {}, options = {}) {
    return FrontdoorInternalService.get(resource, params, options);
  }

  /**
   * POST request with a JSON body.
   * @param {string} resource - The endpoint path
   * @param {Object} data - Request body
   */
  post(resource, data = {}) {
    return FrontdoorInternalService.post(resource, data);
  }

  /**
   * PUT request with a JSON body.
   * @param {string} resource - The endpoint path
   * @param {Object} data - Request body
   */
  put(resource, data = {}) {
    return FrontdoorInternalService.put(resource, data);
  }

  /**
   * DELETE request.
   * @param {string} resource - The full endpoint path including ID, e.g. '/users/123'
   */
  delete(resource) {
    return FrontdoorInternalService.delete(resource);
  }

  /**
   * Multipart POST for file uploads.
   * Sets the correct multipart/form-data boundary automatically.
   * @param {string} resource - The endpoint path
   * @param {FormData} formData - The FormData object containing the file
   */
  postMultipart(resource, formData) {
    return FrontdoorInternalService.postMultipart(resource, formData);
  }

  /**
   * Multipart PUT for file uploads.
   * Sets the correct multipart/form-data boundary automatically.
   * @param {string} resource - The endpoint path
   * @param {FormData} formData - The FormData object containing the file
   */
  putMultipart(resource, formData) {
    return FrontdoorInternalService.putMultipart(resource, formData);
  }
}
